// Catálogo de tipos de campo do builder.
//
// É a fonte única que alimenta a paleta (o que dá para adicionar) e o inspetor
// (o que dá para editar em cada tipo). O JSON produzido aqui é o **mesmo** do
// construtor antigo — o DSL é contrato com o preenchimento e com o PDF, então
// `create()` tem que continuar gerando exatamente as mesmas chaves.
// Ver docs/02-esquema-json.md.

// Campos que o `supports` de cada tipo pode declarar:
//   label        — component.label (rótulo)
//   text         — component.label guarda um texto longo (text-info)
//   identifier   — pode ser o identificador do preenchimento
//   required     — pode ser marcado como obrigatório (tipo que coleta valor)
//   options      — lista de opções (select/select-auto)
//   checkbox     — lista de opções com marcação + observação + multi
//   columns      — colunas (table)
//   image        — upload + alinhamento + escala
//   mediaUrl     — source é uma URL (video/audio)
//   urlApi       — url_api (select-api)
//   extraFields  — campos adicionais por item (product-order / product-table)
//   formLink     — form-alvo + campos trazidos
//   prefillFrom  — copia os itens de um registro vinculado (prefill_from) e
//                  pode exibir os preços copiados (show_prices) — product-table
//   hidePdf      — pode ser omitido do PDF (text-info)
//   signature    — usa index_signature (recalculado automaticamente)

const baseComponent = (type) => ({
  label: "",
  type_input: type === "date" ? "date" : "text",
});

const FIELD_TYPES = [
  {
    type: "input",
    label: "Input Texto",
    group: "Entrada",
    description: "Campo de texto de uma linha.",
    supports: { label: true, identifier: true, required: true },
    create: () => ({ type: "input", component: baseComponent("input") }),
  },
  {
    type: "input-full",
    label: "Input Texto Full",
    group: "Entrada",
    description: "Texto de uma linha ocupando a largura toda.",
    supports: { label: true, identifier: true, required: true },
    create: () => ({ type: "input-full", component: baseComponent("input-full") }),
  },
  {
    type: "date",
    label: "Input Data",
    group: "Entrada",
    description: "Seletor de data (dd/mm/aaaa).",
    supports: { label: true, identifier: true, required: true },
    create: () => ({ type: "date", component: baseComponent("date") }),
  },
  {
    type: "textarea",
    label: "Input Texto Grande",
    group: "Entrada",
    description: "Texto de várias linhas.",
    supports: { label: true, identifier: true, required: true },
    create: () => ({ type: "textarea", component: baseComponent("textarea") }),
  },
  {
    type: "checkbox",
    label: "CheckBox",
    group: "Escolha",
    description: "Lista de marcações, com observação opcional.",
    supports: { label: true, checkbox: true, required: true },
    create: () => ({
      type: "checkbox",
      component: baseComponent("checkbox"),
      options: [{ label: "", type: "checkbox", checked: false }],
    }),
  },
  {
    type: "select",
    label: "Seletor",
    group: "Escolha",
    description: "Lista suspensa de opções fixas.",
    supports: { label: true, identifier: true, options: true, required: true },
    create: () => ({
      type: "select",
      component: baseComponent("select"),
      options: [{ label: "" }],
    }),
  },
  {
    type: "select-auto",
    label: "Select Flexível",
    group: "Escolha",
    description: "Lista com busca; aceita valor fora da lista.",
    supports: { label: true, identifier: true, options: true, required: true },
    create: () => ({
      type: "select-auto",
      component: baseComponent("select-auto"),
      options: [{ label: "" }],
    }),
  },
  {
    type: "select-api",
    label: "Select Via API",
    group: "Escolha",
    description: "Opções carregadas de uma URL externa ([{ id, nome }]).",
    supports: { label: true, identifier: true, urlApi: true, required: true },
    create: () => ({
      type: "select-api",
      component: baseComponent("select-api"),
      options: [],
      url_api: "",
    }),
  },
  {
    type: "select-func",
    label: "Seleção de Funcionários",
    group: "Escolha",
    description: "Escolhe um usuário da empresa.",
    supports: { label: true, identifier: true, required: true },
    create: () => ({ type: "select-func", component: baseComponent("select-func") }),
  },
  {
    type: "select-resp",
    label: "Responsável",
    group: "Escolha",
    description:
      "Define quem enxerga o preenchimento. Sem responsável, todos veem.",
    supports: { label: true, identifier: true, required: true },
    create: () => ({ type: "select-resp", component: baseComponent("select-resp") }),
  },
  {
    type: "table",
    label: "Tabela",
    group: "Estrutura",
    description: "Mini-formulário repetível; cada linha vira um lançamento.",
    supports: { label: true, columns: true, required: true },
    create: () => ({
      type: "table",
      component: baseComponent("table"),
      columns: [],
      body: [],
    }),
  },
  {
    type: "form-link",
    label: "Vínculo de Formulário",
    group: "Estrutura",
    description: "Traz dados de um preenchimento de outro formulário.",
    supports: { label: true, identifier: true, formLink: true, required: true },
    create: () => ({
      type: "form-link",
      component: baseComponent("form-link"),
      target_form_id: "",
      target_form_name: "",
      fields: [],
    }),
  },
  {
    type: "product-order",
    label: "Tabela de Produtos",
    group: "Estrutura",
    description: "Catálogo de produtos com quantidade, preço e totais.",
    supports: { label: true, extraFields: true, required: true },
    create: () => ({
      type: "product-order",
      component: baseComponent("product-order"),
    }),
  },
  {
    type: "product-table",
    label: "Tabela de Produtos (sem valores)",
    group: "Estrutura",
    description: "Só produto, quantidade e peso — sem parte financeira.",
    supports: { label: true, extraFields: true, prefillFrom: true, required: true },
    create: () => ({
      type: "product-table",
      component: baseComponent("product-table"),
    }),
  },
  {
    type: "signature",
    label: "Assinatura",
    group: "Estrutura",
    description: "Área de assinatura à mão.",
    supports: { label: true, signature: true, required: true },
    create: () => ({
      type: "signature",
      component: baseComponent("signature"),
      index_signature: 0,
    }),
  },
  {
    type: "text-info",
    label: "Texto Informativo",
    group: "Conteúdo",
    description: "Bloco de texto só de leitura.",
    supports: { text: true, hidePdf: true },
    create: () => ({ type: "text-info", component: baseComponent("text-info") }),
  },
  {
    type: "title",
    label: "Título",
    group: "Conteúdo",
    description: "Título de seção.",
    supports: { label: true },
    create: () => ({ type: "title", component: baseComponent("title") }),
  },
  {
    type: "title-black",
    label: "Título Black Bar",
    group: "Conteúdo",
    description: "Título de seção em faixa.",
    supports: { label: true },
    create: () => ({ type: "title-black", component: baseComponent("title-black") }),
  },
  {
    type: "image",
    label: "Imagem",
    group: "Conteúdo",
    // A imagem não tem `component` no DSL — o upload é o próprio conteúdo.
    description: "Imagem fixa no formulário (upload).",
    supports: { image: true },
    create: () => ({ type: "image", source: "", centered: false, scale: 100 }),
  },
  {
    type: "video",
    label: "Video",
    group: "Conteúdo",
    description: "Player de vídeo a partir de uma URL.",
    supports: { label: true, mediaUrl: true },
    create: () => ({ type: "video", component: baseComponent("video"), source: "" }),
  },
  {
    type: "audio",
    label: "Audio",
    group: "Conteúdo",
    description: "Player de áudio a partir de uma URL.",
    supports: { label: true, mediaUrl: true },
    create: () => ({ type: "audio", component: baseComponent("audio"), source: "" }),
  },
];

// Tipos aceitos como coluna de tabela (o preenchimento só sabe renderizar estes
// dentro de uma tabela — ver CustomForm, case "table").
const COLUMN_TYPES = [
  { type: "input", label: "Input Texto" },
  { type: "date", label: "Input Data" },
  { type: "checkbox", label: "CheckBox" },
  { type: "textarea", label: "Input Texto Grande" },
  { type: "select", label: "Seletor" },
  { type: "signature", label: "Assinatura" },
  { type: "select-func", label: "Seleção de Funcionários" },
];

const FIELD_TYPES_BY_TYPE = FIELD_TYPES.reduce((acc, definition) => {
  acc[definition.type] = definition;
  return acc;
}, {});

const GROUPS = FIELD_TYPES.reduce((acc, definition) => {
  if (!acc.includes(definition.group)) acc.push(definition.group);
  return acc;
}, []);

// Definição de um tipo. Tipos legados que saíram da paleta (table-info, file)
// continuam existindo em layouts antigos: devolvemos um fallback para o builder
// conseguir listar e mover o campo sem destruí-lo.
function getFieldDefinition(type) {
  return (
    FIELD_TYPES_BY_TYPE[type] || {
      type,
      label: type,
      group: "Legado",
      description: "Tipo legado: dá para mover e remover, mas não editar aqui.",
      supports: { label: true },
      create: () => ({ type }),
    }
  );
}

function createField(type) {
  return getFieldDefinition(type).create();
}

function supports(field, capability) {
  if (!field) return false;
  return !!getFieldDefinition(field.type).supports[capability];
}

// Nova coluna de tabela, na mesma gramática de um campo.
function createColumn(type) {
  const column = {
    type,
    component: { label: "", type_input: type === "date" ? "date" : "text" },
  };

  if (type === "checkbox") {
    column.options = [{ label: "", type: "checkbox", checked: false }];
  }
  if (type === "select") {
    column.options = [{ label: "" }];
  }
  if (type === "signature") {
    column.index_signature = 0;
  }

  return column;
}

// Rótulo de um campo para listas do builder (o texto informativo não tem
// "rótulo": mostramos o começo do próprio texto).
function fieldLabel(field) {
  if (!field) return "";
  const label = (field.component && field.component.label) || "";
  if (label) return label.length > 60 ? `${label.slice(0, 60)}…` : label;
  if (field.type === "image") return field.source || "(sem imagem)";
  return "(sem rótulo)";
}

export {
  FIELD_TYPES,
  COLUMN_TYPES,
  GROUPS,
  getFieldDefinition,
  createField,
  createColumn,
  supports,
  fieldLabel,
};
